import { FC, useCallback, useEffect, useContext, useState, useMemo } from 'react';
import { Box, Button, Typography, Select } from '@robocorp/components';
import { useTranslation } from 'react-i18next';
import { useActions, useSelector } from '~/hooks';
import { CurrentWorkspaceRestriction } from '~/components';
import { mapInternalEndpointPermissions } from '~/types/WorkspaceKeyTypes';
import { ApiHelperContext } from '../context';

export const SelectApiKey: FC = () => {
  const { t } = useTranslation();

  const [key, setKey] = useState<string | undefined>(undefined);
  const { endpoint, pathParameters, setPathParameter } = useContext(ApiHelperContext);

  const {
    creatingWorkspaceKey,
    dialogOpen,
    loadingWorkspaceKey,
    loadingWorkspaceKeys,
    workspaceKeyById,
  } = useSelector(({ entityStates, ui, workspaceResources }) => ({
    loadingWorkspaceKeys: entityStates.workspaceKeys.loading,
    creatingWorkspaceKey: entityStates.workspaceKeys.creating,
    loadingWorkspaceKey: entityStates.workspaceKeys.byId,
    dialogOpen: ui.wsKeyDialogState.dialogOpen,
    workspaceKeyById: workspaceResources.workspaceKeys.byId,
  }));

  const { getWorkspaceKey, getWorkspaceKeys, toggleWsKeyDialog } = useActions(
    ({ uiActions, workspaceKeyActions }) => ({
      getWorkspaceKey: workspaceKeyActions.getWorkspaceKey,
      getWorkspaceKeys: workspaceKeyActions.getWorkspaceKeys,
      toggleWsKeyDialog: uiActions.toggleWsKeyDialog,
    }),
  );

  const apiKeysItems = useMemo(() => {
    const endpointPermissions = mapInternalEndpointPermissions(endpoint?.permissions);
    return Object.values(workspaceKeyById)
      .filter(
        (item) =>
          endpointPermissions &&
          endpointPermissions.every((permission) => item.permissions.includes(permission)),
      )
      .map(({ name, id }) => ({ label: name, value: id }));
  }, [endpoint, workspaceKeyById]);

  const hasValidApiKey = apiKeysItems.length > 0 && !loadingWorkspaceKeys;

  useEffect(() => {
    getWorkspaceKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasValidApiKey) {
      setPathParameter('api_key', undefined);
    }
  }, [hasValidApiKey, setPathParameter]);

  useEffect(() => {
    if (endpoint.permissions && hasValidApiKey) {
      const keySet = Object.values(workspaceKeyById).find(
        (item) =>
          item.id === key &&
          endpoint.permissions.every((permission) => item.permissions.includes(permission)),
      );
      if (keySet) {
        return;
      }

      const keyExists = Object.values(workspaceKeyById).find((item) =>
        endpoint.permissions.every((permission) => item.permissions.includes(permission)),
      );
      if (keyExists && keyExists.id !== key) {
        setKey(keyExists.id);
        if (!keyExists.secret) {
          getWorkspaceKey(keyExists.id);
        } else {
          setPathParameter('api_key', keyExists.secret);
        }
      }
    }
  }, [
    endpoint.permissions,
    getWorkspaceKey,
    hasValidApiKey,
    key,
    setPathParameter,
    workspaceKeyById,
  ]);

  useEffect(() => {
    if (key && hasValidApiKey) {
      const workspaceKey = workspaceKeyById[key];
      if (workspaceKey?.secret && pathParameters.api_key !== workspaceKey.secret) {
        setPathParameter('api_key', workspaceKey.secret);
      }
    }
  }, [hasValidApiKey, key, pathParameters.api_key, setPathParameter, workspaceKeyById]);

  const onChange = useCallback(
    (value: string) => {
      const workspaceKey = workspaceKeyById[value];

      setKey(value);

      if (workspaceKey?.secret && pathParameters.api_key !== workspaceKey.secret) {
        setPathParameter('api_key', workspaceKey?.secret);
      }

      if (!workspaceKey?.secret) {
        getWorkspaceKey(value);
        setPathParameter('api_key', undefined);
      }
    },
    [getWorkspaceKey, pathParameters.api_key, setPathParameter, workspaceKeyById],
  );

  const onCreateWorkspaceKey = useCallback(() => {
    toggleWsKeyDialog(undefined, mapInternalEndpointPermissions(endpoint.permissions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint.permissions]);

  if (typeof key === 'string' && loadingWorkspaceKey[key]?.loading) {
    return (
      <Box mb="$16">
        <Select
          label={t('APIHelper.chooseKey')}
          items={[]}
          disabled
          placeholder={t('APIHelper.requestingKey')}
        />
      </Box>
    );
  }

  return (
    <Box mb="$16">
      {!hasValidApiKey && !creatingWorkspaceKey ? (
        <>
          {endpoint.permissions.length > 0 && (
            <Typography mb={12} color="grey20">
              {t('APIHelper.noKeyWithPermission', { permission: endpoint.permissions.join(',') })}
            </Typography>
          )}
          <CurrentWorkspaceRestriction permission="write_workspace_keys">
            <Button onClick={onCreateWorkspaceKey} disabled={dialogOpen}>
              {t('APIHelper.createKey')}
            </Button>
          </CurrentWorkspaceRestriction>
        </>
      ) : (
        <Select
          label={t('APIHelper.chooseKey')}
          searchable
          items={apiKeysItems}
          onChange={onChange}
          disabled={loadingWorkspaceKeys || creatingWorkspaceKey}
          value={key}
          placeholder={
            loadingWorkspaceKeys || creatingWorkspaceKey ? t('loading') : `${t('choose')}...`
          }
        />
      )}
    </Box>
  );
};
